import * as CardUtils from '../utils/CardUtils';
import {getDeviceType, isMobile} from '../utils/BrowserUtils';
import _ from 'lodash';
import {getFrameId} from '../ad/utils';
import {renderModes, displayModes, snippetDefaults} from '../const/AugmentationConst';
import moment from 'moment';

export const collectionItemOrderSort = item => (_.isNull(item.order) ? Number.MAX_VALUE : item.order);

export default function parseConfigs(collection) {
  return _.flatMap(collection.slots, (slot) => {
    return _(slot.slotCampaigns)
    .filter(slotCampaign => slotCampaign.isActive && slotCampaign.campaign && isCampaignActive(slotCampaign.campaign))
    .uniqBy(slotCampaign => _.get(slotCampaign, 'campaign.id'))
    .sortBy(collectionItemOrderSort)
    .flatMap((slotCampaign) => {
      const {campaign} = slotCampaign;
      let renderers = getPlainRendererConfigs(slotCampaign, collection);
      renderers = renderers.map(r => mergeWithCampaignConfig(r, campaign));

      if (!_.isEmpty(campaign.cards)) {
        renderers = _(campaign.cards)
          .filter(cardItem => cardItem.isActive)
          .uniqBy(cardItem => _.get(cardItem, 'card.id'))
          .sortBy(collectionItemOrderSort)
          .flatMap((cardItem) => {
            return _(renderers)
              .map((campaignRenderer) => {
                return mergeWithCardConfig(campaignRenderer, cardItem.card, campaign);
              })
              .compact()
              .value();
          })
          .value();
      }

      renderers = _(renderers)
        .filter({isActive: true})
        .filter(r => _.isEmpty(r.devices) || r.devices.includes(getDeviceType()))
        .map((r) => {
          const cardId = r.cardId || null;
          const frameId = ([renderModes.iframeVupopup, renderModes.iframeBottomBanner].includes(r.renderMode)) ?
            getFrameId(r.rendererId, null, r.campaignId) : // Ad iframes don't use cards
            getFrameId(r.rendererId, cardId, r.campaignId);
            const adRendererOverrides = {};
            const renderSearchPhrases = _.get(r, 'adRenderer.renderSearchPhrases');
            if (!_.isEmpty(renderSearchPhrases)) {
              adRendererOverrides.renderSearchPhrases = renderSearchPhrases;
            }
            const renderSearchPhraseRegex = _.get(r, 'adRenderer.renderSearchPhraseRegex');
            if (renderSearchPhraseRegex) {
              adRendererOverrides.renderSearchPhraseRegex = renderSearchPhraseRegex;
            }

          return {
            ...r,
            ...adRendererOverrides,
            record: r.record || {},
            cardId,
            frameId,
          };
        })
        .map(r => mergeWithPublisherSlotConfig(r, slot, collection))
        .compact()
        .value();

      return formatConfigFields(renderers);
  })
  .value();
  });
}

function isCampaignActive(campaign) {
  if (!campaign.isActive) {
    return false;
  }

  const now = moment();
  if (campaign.activeStartDate && moment(campaign.activeStartDate).isAfter(now, 'day')) {
    return false;
  }
  if (campaign.activeEndDate && moment(campaign.activeEndDate).isBefore(now, 'day')) {
    return false;
  }

  const nowTime = now.format('HH:mm');
  const activeStartTime = campaign.activeStartTime || '00:00';
  const activeEndTime = campaign.activeEndTime || '23:59:59';

  if (activeStartTime <= activeEndTime) {
    if (nowTime < activeStartTime || nowTime > activeEndTime) {
      return false;
    }
  } else if (nowTime < activeStartTime && nowTime > activeEndTime) {
      return false;
  }
  return true;
}

export function getCampaignRenderers(campaign) {
  const renderers = _.get(campaign, 'renderers', []);
  return _(renderers)
    .sortBy(collectionItemOrderSort)
    .filter({isActive: true})
    .value();
}

function getPlainRendererConfigs(publisherCampaign, collection) {
  const {campaign, adTrackingId} = publisherCampaign;
  const renderers = getCampaignRenderers(campaign);
  const result = _(renderers)
    .filter(renderer => (
      (collection ? renderer.collectionExclusions.indexOf(collection.id) < 0 : true)
    ))
    .map((renderer) => {
      const config = _.pickBy(_.pick(renderer, [
        'id',
        'isActive',
        'devices',
        'displayMode',
        'trackingCodes',
        'actionMode',
        'actionUrl',
        'triggerMode',
        'triggerText',
        'triggerButton',
        'triggerCustomClass',
        'renderMode',
        'renderSearchSelector',
        'renderHeaderSelector',
        'vulineCallToAction',
        'vulineImage',
        'isVulinePosterEnabled',
        'exclusionPhrases',
        'collectionExclusions',
        'vupopupTimeoutDuration',
        'vupopupDefaultTimeoutDuration',
        'displayCount',
        'displayFrequency',
        'lifetimeDisplayCount',
        'firstDisplay',
        'displayDuration',
        'delayDuration',
        'versionHash',
        'iframeToken',
        'affiliateType',
        'adSize',
        'iframeCode',
        'iframeUrl',
        'templateName',
        'useAdTemplateContent',
        'adTemplate',
        'adRenderer',
        'adRefreshInterval',
        'productRefreshInterval',
        'programmaticAdTimeToShow'
      ]), (prop) => {
        return (!_.isNull(prop) && !_.isUndefined(prop) && prop !== '');
      });
      config.rendererId = renderer.id;
      config.adTrackingId = adTrackingId;
      return config;
    })
    .value();

  return result;
}

function mergeWithCampaignConfig(rendererConfig, campaign) {
  const update = {..._.omit(campaign, ['id', 'renderers', 'cards']), campaignId: campaign.id};

  return {
    ...update,
    ...rendererConfig,
    campaign
  };
}

function mergeWithCardConfig(rendererConfig, card) {
  const update = {
    ..._.omit(card, ['id', 'renderers', 'exclusionSelector']),
    cardId: card.id,
  };

  const result = {
    ...update,
    ...rendererConfig
  };

  return {
    ...result,
    record: CardUtils.mergeCardWithProgram(card, card.program)
  };
}

function mergeWithPublisherSlotConfig(rendererConfig, slot, collection) {
  let update;
  let vulineSubheading;
  let renderHeaderSelector;

  if (rendererConfig.renderMode === renderModes.vuline) {
    const deviceMode = isMobile.any() ? 'mobile' : 'desktop';
    const vulineRenderer = _.find(slot.vulineRenderers, vr => _.includes(vr.devices, deviceMode));

    if (vulineRenderer) {
      vulineSubheading = vulineRenderer.subheading;
      renderHeaderSelector = vulineRenderer.headerSelector;
    } else {
      return null;
    }
  }

  update = {
    collectionId: collection.id,
    publisherProductSources: collection.publisherProductSources,
    collection,
    slotId: slot.id,
    slot,
    exclusionSelector: slot.exclusionSelector,
    renderCount: slot.vuphraseRenderCount,
    allowMultipleVulinesOnPage: slot.allowMultipleVulinesOnPage,
    vulineSubheading,
    renderHeaderSelector
  };

  switch (rendererConfig.renderMode) {
    case renderModes.vuphrase:
      update.renderSearchSelector = slot.vuphraseSearchSelector;
      break;
    case renderModes.vuline:
      update.renderSearchSelector = slot.vulineSearchSelector;
      break;
    case renderModes.vupopup:
      update.renderSearchSelector = slot.vupopupSearchSelector;
      break;
  }

  return {
    ...update,
    ...rendererConfig
  };
}

function formatConfigFields(rendererConfigs) {
  return rendererConfigs.map((config) => {
    //apply toLowerCase on search phrases
    if (!_.isEmpty(config.renderSearchPhrases)) {
      config.renderSearchPhrases = config.renderSearchPhrases.map(singleList => singleList.map(s => s.toLowerCase()));
    }
    if (!_.isEmpty(config.exclusionPhrases)) {
      config.exclusionPhrases = config.exclusionPhrases.map(singleList => singleList.map(s => s.toLowerCase()));
    }
    if (!_.isEmpty(config.cardExclusionPhrases)) {
      config.cardExclusionPhrases = config.cardExclusionPhrases.map(singleList => singleList.map(s => s.toLowerCase()));
    }

    if (config.renderMode !== renderModes.vuphrase && config.displayMode === displayModes.phrase) {
      //displayMode "phrase" is invalid for any config.renderMode other than "vuphrase"
      config.displayMode = null;
    }

    if (config.renderMode === renderModes.vuline && config.vulineSubheading) {
      config.vulineSubheading = interpolateVulineTemplate(config.vulineSubheading, config);
    }

    config.renderHeaderSelector = config.renderHeaderSelector || snippetDefaults.headingSelector;
    config.renderSearchSelector = config.renderSearchSelector || snippetDefaults.searchSelector;

    config.vupopupTimeoutDuration = (config.vupopupTimeoutDuration !== 0 && !config.vupopupTimeoutDuration) ?
      config.vupopupDefaultTimeoutDuration : config.vupopupTimeoutDuration;

    return config;
  });
}

function interpolateVulineTemplate(template, rendererConfig) {
 return template
    .replace('{cta}', _.get(rendererConfig, 'vulineCallToAction', '{cta}'))
    .replace('{title}', _.get(rendererConfig, 'record.title', '{title}'))
    .replace('{network.name}', _.get(_.find(_.get(rendererConfig.record, 'destinations', []), {kind: 'network'}), 'name', '{network.name}'));
}
